import { createAsyncThunk } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';
import _flatten from 'lodash/flatten';

import { COURSE_STRUCTURES } from 'constants/common';

import { updateLessonStatusViaApi } from 'store/helpers/updateLessonStatusViaApi';

import * as api from 'api/methods';

import * as types from './types';

const _getCourseModules = async (courseId: string) => {
  const promises = [
    api.getCourseModules(courseId),
    api.getCourseLessons(courseId),
  ];

  const [modules, lessons] = await Promise.all(promises);

  return _orderBy(modules, ['order'], ['asc']).map((module: any) => ({
    ...module,
    lessons: lessons.filter((lesson: any) => lesson.moduleId === module.id),
  }));
};

export const getCourseById = createAsyncThunk<any, any>(
  types.GET_CURRENT_COURSE,
  async (courseId: string) => {
    const course = await api.getCourseById(courseId);

    if (course.structure === COURSE_STRUCTURES.MODULES) {
      const modules = await _getCourseModules(courseId);

      return {
        ...course,
        modules,
      };
    }

    if (course.structure === COURSE_STRUCTURES.LESSONS) {
      const lessons = await api.getCourseLessons(courseId);

      return { ...course, lessons };
    }

    return course;
  },
);

export const getDailyChallengeForEditor = createAsyncThunk<any, any>(
  types.GET_DAILY_CHALLENGE_FOR_EDITOR,
  async (courseId: string) => {
    const course = await api.getDailyChallengeById(courseId);

    if (course.structure === COURSE_STRUCTURES.MODULES) {
      const modules = await _getCourseModules(courseId);

      return {
        ...course,
        modules,
      };
    }

    if (course.structure === COURSE_STRUCTURES.LESSONS) {
      const lessons = await api.getCourseLessons(courseId);

      return { ...course, lessons };
    }

    return course;
  },
);

export const updateCourseById = createAsyncThunk<any, any>(
  types.UPDATE_COURSE_BY_ID,
  async ({ courseId, data }: { courseId: string; data: any }) => {
    const course = await api.updateCourseById({
      courseId,
      data,
    });

    if (data.structure && data.structure === COURSE_STRUCTURES.MODULES) {
      const modules = await _getCourseModules(courseId);

      return { ...course, modules };
    }

    if (data.structure === COURSE_STRUCTURES.LESSONS) {
      const lessons = await api.getCourseLessons(courseId);

      return { ...course, lessons };
    }

    return course;
  },
);

export const updateCourseStatus = createAsyncThunk<any, any>(
  types.UPDATE_COURSE_STATUS,
  async (
    {
      courseId,
      withLessons,
      status,
    }: { courseId: string; withLessons: any; status: any },
    { getState }: { getState: any },
  ) => {
    if (withLessons) {
      const state = getState() as {
        currentCourse: {
          structure?: string;
          modules?: { lessons: unknown[] }[];
          lessons?: unknown[];
        };
      };

      let lessons = [];

      if (state.currentCourse.structure === COURSE_STRUCTURES.MODULES) {
        lessons = _flatten(
          state.currentCourse.modules.map((module: any) => module.lessons),
        );
      } else if (state.currentCourse.structure === COURSE_STRUCTURES.LESSONS) {
        lessons = state.currentCourse.lessons;
      }

      await Promise.all(
        lessons.map((lesson: any) =>
          updateLessonStatusViaApi({
            lessonId: lesson.id,
            lessonType: lesson.type,
            data: { status },
          }),
        ),
      );
    }

    const course = await api.updateCourseById({
      courseId,
      data: { status },
    });

    return {
      ...course,
      bulkLessonStatus: withLessons ? status : undefined,
    };
  },
);

export const updateDailyChallengeById = createAsyncThunk<any, any>(
  types.UPDATE_DAILY_CHALLENGE_BY_ID,
  async ({ courseId, data }: { courseId: string; data: any }) => {
    const course = await api.updateDailyChallengeById({
      dailyChallengeId: courseId,
      data,
    });

    if (data.structure && data.structure === COURSE_STRUCTURES.MODULES) {
      const modules = await _getCourseModules(courseId);

      return { ...course, modules };
    }

    if (data.structure === COURSE_STRUCTURES.LESSONS) {
      const lessons = await api.getCourseLessons(courseId);

      return { ...course, lessons };
    }

    return course;
  },
);

export const updateDailyChallengeStatus = createAsyncThunk<any, any>(
  types.UPDATE_DAILY_CHALLENGE_STATUS,
  async (
    {
      courseId,
      withLessons,
      status,
    }: { courseId: string; withLessons: any; status: any },
    { getState }: { getState: any },
  ) => {
    if (withLessons) {
      const state = getState() as {
        currentCourse: {
          structure?: string;
          modules?: { lessons: unknown[] }[];
          lessons?: unknown[];
        };
      };

      let lessons = [];

      if (state.currentCourse.structure === COURSE_STRUCTURES.MODULES) {
        lessons = _flatten(
          state.currentCourse.modules.map((module: any) => module.lessons),
        );
      } else if (state.currentCourse.structure === COURSE_STRUCTURES.LESSONS) {
        lessons = state.currentCourse.lessons;
      }

      await Promise.all(
        lessons.map((lesson: any) =>
          updateLessonStatusViaApi({
            lessonId: lesson.id,
            lessonType: lesson.type,
            data: { status },
          }),
        ),
      );
    }

    const course = await api.updateDailyChallengeById({
      dailyChallengeId: courseId,
      data: { status },
    });

    return {
      ...course,
      bulkLessonStatus: withLessons ? status : undefined,
    };
  },
);

export const createCourseModule = createAsyncThunk<any, any>(
  types.CREATE_COURSE_MODULE,
  async ({ courseId, data }: { courseId: string; data: any }) => {
    const module = await api.createCourseModule({
      courseId,
      data,
    });

    return module;
  },
);

export const updateCourseModuleById = createAsyncThunk<any, any>(
  types.UPDATE_COURSE_MODULE_BY_ID,
  async ({
    courseId,
    moduleId,
    data,
  }: {
    courseId: string;
    moduleId: string;
    data: any;
  }) => {
    const module = await api.updateCourseModuleById({
      courseId,
      moduleId,
      data,
    });

    return module;
  },
);

export const removeCourseModuleById = createAsyncThunk<any, any>(
  types.REMOVE_COURSE_MODULE_BY_ID,
  async ({ courseId, moduleId }: { courseId: string; moduleId: string }) => {
    const module = await api.removeCourseModuleById({ courseId, moduleId });

    return module;
  },
);

export const reorderCourseLessons = createAsyncThunk<any, any>(
  types.REORDER_COURSE_LESSONS,
  async ({ courseId, data }: { courseId: string; data: any }) => {
    const lessons = await api.reorderCourseLessons({ courseId, data });

    return { moduleId: data.moduleId, lessons };
  },
);

export const reorderCourseModules = createAsyncThunk<any, any>(
  types.REORDER_COURSE_MODULES,
  async ({ courseId, data }: { courseId: string; data: any }) => {
    const modules = await api.reorderCourseModules({ courseId, data });

    return modules;
  },
);
